import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, Check, Ruler, Car } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const inputCls =
    "bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

const addBtnCls =
    "flex items-center gap-1.5 bg-amber-400 text-[#0A1228] font-extrabold text-xs rounded-full px-4 py-2 hover:bg-amber-300 transition-colors duration-300";

const iconBtnCls =
    "p-1.5 rounded-lg border border-white/10 text-slate-400 transition-colors duration-200";

export const InquiryPanel = () => (
    <div className="space-y-10" data-testid="admin-inquiry-panel">
        <div>
            <h2 className="font-display font-bold text-xl text-white">نموذج الاستفسار</h2>
            <p className="text-sm text-slate-500 mt-1">
                تحكم كامل في خيارات نموذج الاستفسار — كل تغيير تحفظه يظهر فوراً للعملاء في الموقع
            </p>
        </div>
        <NameListManager
            title="نوعيات الإطارات"
            subtitle="مثل: صيفي، شتوي، رياضي، شاحنات — تظهر في قائمة «نوعية الإطار» داخل النموذج"
            endpoint="/inquiry/types"
            testid="inquiry-types"
        />
        <NameListManager
            title="ماركات الإطارات"
            subtitle="الماركات الظاهرة في قائمة «ماركة الإطار» داخل النموذج"
            endpoint="/inquiry/brands"
            testid="inquiry-brands"
        />
        <OptionsEditor />
        <CarTreeManager />
    </div>
);

function NameListManager({ title, subtitle, endpoint, testid }) {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
    }, [endpoint]);

    useEffect(() => { load(); }, [load]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { name_ar: form.name_ar, name_en: form.name_en, order: Number(form.order) || 0 };
        try {
            if (form.id) await api.put(`${endpoint}/${form.id}`, payload);
            else await api.post(endpoint, payload);
            toast.success("تم الحفظ — يظهر التغيير فوراً في الموقع");
            setForm(null);
            load();
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`حذف «${item.name_ar}»؟`)) return;
        try {
            await api.delete(`${endpoint}/${item.id}`);
            toast.success("تم الحذف");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    return (
        <section className="spotlight-card border border-white/10 rounded-3xl p-6" data-testid={`${testid}-section`}>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-bold text-white">{title}</h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
                </div>
                {!form && (
                    <button
                        onClick={() => setForm({ name_ar: "", name_en: "", order: items.length + 1 })}
                        data-testid={`${testid}-add-button`}
                        className={addBtnCls}
                    >
                        <Plus className="w-3.5 h-3.5" /> إضافة
                    </button>
                )}
            </div>
            {form && (
                <form onSubmit={save} className="grid sm:grid-cols-4 gap-3 mb-4 border border-amber-400/30 rounded-2xl p-4" data-testid={`${testid}-form`}>
                    <input required value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} placeholder="الاسم (عربي)" data-testid={`${testid}-name-ar`} className={inputCls} />
                    <input required value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} placeholder="الاسم (إنجليزي)" data-testid={`${testid}-name-en`} className={inputCls} dir="ltr" />
                    <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="الترتيب" data-testid={`${testid}-order`} className={inputCls} dir="ltr" />
                    <div className="flex gap-2">
                        <button type="submit" disabled={saving} data-testid={`${testid}-save`} className="flex-1 flex items-center justify-center gap-1.5 bg-amber-400 text-[#0A1228] font-extrabold text-xs rounded-xl py-2.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50">
                            <Check className="w-3.5 h-3.5" /> {form.id ? "حفظ" : "إضافة"}
                        </button>
                        <button type="button" onClick={() => setForm(null)} className={`${iconBtnCls} hover:text-white`} aria-label="إلغاء">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </form>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between border border-white/10 rounded-xl px-4 py-3 bg-[#101B3D]/60" data-testid={`${testid}-row`}>
                        <div className="min-w-0">
                            <p className="text-sm font-bold text-white truncate">{item.name_ar}</p>
                            <p className="text-[10px] text-slate-500 truncate" dir="ltr">{item.name_en}</p>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                            <button onClick={() => setForm({ ...item })} data-testid={`${testid}-edit`} className={`${iconBtnCls} hover:text-amber-400 hover:border-amber-400/50`} aria-label="تعديل">
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => remove(item)} data-testid={`${testid}-delete`} className={`${iconBtnCls} hover:text-red-400 hover:border-red-500/50`} aria-label="حذف">
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

const OPTION_GROUPS = [
    { key: "widths", label: "قيم العرض — النظام المتري", hint: "مثال: 205" },
    { key: "heavy_widths", label: "العروض الثقيلة (شاحنات)", hint: "يُخفى حقل الارتفاع تلقائياً عند اختيارها — مثال: 825" },
    { key: "heights", label: "قيم الارتفاع", hint: "مثال: 65" },
    { key: "rims", label: "مقاسات الجنط (R)", hint: "مثال: R17" },
    { key: "inch_dias", label: "القطر الكلي بالبوصة — النظام الأمريكي", hint: "مثال: 35" },
    { key: "inch_widths", label: "العرض بالبوصة — النظام الأمريكي", hint: "مثال: 12.50" },
    { key: "years", label: "سنوات الموديلات", hint: "تظهر في اختيار «حسب السيارة» — مثال: 2025" },
];

function OptionsEditor() {
    const [opts, setOpts] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/inquiry/options").then((r) => setOpts(r.data)).catch(() => setOpts({}));
    }, []);

    if (!opts) return null;

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/inquiry/options", opts);
            toast.success("تم حفظ المقاسات — تظهر فوراً في الموقع");
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-5" data-testid="inquiry-options-section">
            <div>
                <h3 className="font-display font-bold text-white flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-amber-400" /> قيم المقاسات والسنوات
                </h3>
                <p className="text-[11px] text-slate-500 mt-0.5">أضف أو احذف أي قيمة ثم اضغط «حفظ المقاسات»</p>
            </div>
            {OPTION_GROUPS.map((g) => (
                <ChipListEditor
                    key={g.key}
                    label={g.label}
                    hint={g.hint}
                    values={opts[g.key] || []}
                    onChange={(v) => setOpts((o) => ({ ...o, [g.key]: v }))}
                    testid={`opt-${g.key}`}
                />
            ))}
            <button onClick={save} disabled={saving} data-testid="inquiry-options-save" className="w-full bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl py-3 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50">
                {saving ? "جاري الحفظ..." : "حفظ المقاسات"}
            </button>
        </section>
    );
}

function ChipListEditor({ label, hint, values, onChange, testid }) {
    const [draft, setDraft] = useState("");

    const add = () => {
        const v = draft.trim();
        if (!v) return;
        if (!values.includes(v)) onChange([...values, v]);
        setDraft("");
    };

    return (
        <div className="border border-white/10 rounded-2xl p-4 space-y-3" data-testid={testid}>
            <p className="text-xs font-bold text-slate-300">
                {label} <span className="text-slate-600 font-normal">— {hint}</span>
            </p>
            <div className="flex flex-wrap gap-2">
                {values.map((v) => (
                    <span key={v} className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/25 text-amber-300 text-xs font-bold rounded-full px-3 py-1" dir="ltr" data-testid={`${testid}-chip`}>
                        {v}
                        <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} data-testid={`${testid}-remove`} aria-label={`حذف ${v}`} className="text-amber-400/60 hover:text-red-400 transition-colors duration-200">
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                {values.length === 0 && <span className="text-[11px] text-slate-600">لا توجد قيم</span>}
            </div>
            <div className="flex gap-2">
                <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="أضف قيمة جديدة" data-testid={`${testid}-input`} className={`${inputCls} flex-1`} dir="ltr" />
                <button type="button" onClick={add} data-testid={`${testid}-add`} className="flex items-center gap-1 bg-amber-400/15 border border-amber-400/40 text-amber-300 text-xs font-extrabold rounded-xl px-4 hover:bg-amber-400 hover:text-[#0A1228] transition-colors duration-300">
                    <Plus className="w-3.5 h-3.5" /> إضافة
                </button>
            </div>
        </div>
    );
}

function CarTreeManager() {
    const [brands, setBrands] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [draft, setDraft] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modelIdx, setModelIdx] = useState(null);
    const [newBrand, setNewBrand] = useState(null);
    const [newModel, setNewModel] = useState(null);
    const [newTrim, setNewTrim] = useState(null);
    const [editModel, setEditModel] = useState(null);
    const [editTrim, setEditTrim] = useState(null);

    const load = useCallback(() => {
        api.get("/inquiry/cars").then((r) => setBrands(r.data.brands || [])).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const select = (b) => {
        if (dirty && !window.confirm("لديك تغييرات غير محفوظة — تجاهلها؟")) return;
        setSelectedId(b.id);
        setDraft(JSON.parse(JSON.stringify({ ar: b.ar, en: b.en, models: b.models || [], order: b.order || 0 })));
        setModelIdx(null);
        setEditModel(null);
        setEditTrim(null);
        setDirty(false);
    };

    const mutate = (fn) => {
        setDraft((d) => {
            const c = JSON.parse(JSON.stringify(d));
            fn(c);
            return c;
        });
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/inquiry/car-brands/${selectedId}`, draft);
            toast.success("تم الحفظ — يظهر التغيير فوراً في الموقع");
            setDirty(false);
            load();
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    const addBrand = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/inquiry/car-brands", { ar: newBrand.ar, en: newBrand.en, models: [], order: brands.length + 1 });
            toast.success("تمت إضافة ماركة السيارة");
            setNewBrand(null);
            await load();
            setSelectedId(data.id);
            setDraft({ ar: data.ar, en: data.en, models: [], order: data.order });
            setModelIdx(null);
            setDirty(false);
        } catch {
            toast.error("تعذر الحفظ");
        }
    };

    const removeBrand = async (b) => {
        if (!window.confirm(`حذف ماركة «${b.ar}» وكل موديلاتها وفئاتها؟`)) return;
        try {
            await api.delete(`/inquiry/car-brands/${b.id}`);
            toast.success("تم الحذف");
            if (selectedId === b.id) {
                setSelectedId("");
                setDraft(null);
                setModelIdx(null);
            }
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    const addModel = (e) => {
        e.preventDefault();
        mutate((c) => c.models.push({ ar: newModel.ar, en: newModel.en, trims: [] }));
        setNewModel(null);
    };

    const addTrim = (e) => {
        e.preventDefault();
        mutate((c) => c.models[modelIdx].trims = [...(c.models[modelIdx].trims || []), { ar: newTrim.ar, en: newTrim.en }]);
        setNewTrim(null);
    };

    const selected = brands.find((b) => b.id === selectedId);

    return (
        <section className="spotlight-card border border-white/10 rounded-3xl p-6 space-y-5" data-testid="inquiry-cars-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-bold text-white flex items-center gap-2">
                        <Car className="w-4 h-4 text-amber-400" /> السيارات (حسب السيارة)
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                        ماركة السيارة ← الموديلات ← الفئات — سنوات الموديلات تُدار من قسم المقاسات أعلاه
                    </p>
                </div>
                {draft && (
                    <button onClick={save} disabled={saving || !dirty} data-testid="car-save-button" className="flex items-center gap-1.5 bg-amber-400 text-[#0A1228] font-extrabold text-xs rounded-full px-5 py-2.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-40">
                        <Check className="w-3.5 h-3.5" /> {saving ? "جاري الحفظ..." : dirty ? "حفظ التعديلات" : "لا توجد تغييرات"}
                    </button>
                )}
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="border border-white/10 rounded-2xl p-4 space-y-2">
                    <p className="text-xs font-bold text-slate-300 mb-2">ماركات السيارات ({brands.length})</p>
                    <div className="max-h-80 overflow-y-auto space-y-1.5 pl-1">
                        {brands.map((b) => (
                            <div key={b.id} className={`flex items-center justify-between rounded-xl px-3 py-2 border transition-colors duration-200 ${selectedId === b.id ? "border-amber-400/60 bg-amber-400/10" : "border-white/5 bg-white/[0.02] hover:border-white/20"}`} data-testid="car-brand-row">
                                <button onClick={() => select(b)} data-testid={`car-brand-select`} className="flex-1 text-right text-sm font-bold text-white truncate">
                                    {b.ar} <span className="text-[10px] text-slate-500 font-normal" dir="ltr">{b.en}</span>
                                </button>
                                <button onClick={() => removeBrand(b)} data-testid="car-brand-delete" className={`${iconBtnCls} hover:text-red-400 hover:border-red-500/50 shrink-0`} aria-label={`حذف ${b.ar}`}>
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                    {newBrand ? (
                        <form onSubmit={addBrand} className="space-y-2 pt-2 border-t border-white/10" data-testid="car-brand-form">
                            <input required value={newBrand.ar} onChange={(e) => setNewBrand({ ...newBrand, ar: e.target.value })} placeholder="الماركة (عربي)" data-testid="car-brand-name-ar" className={`${inputCls} w-full`} />
                            <input required value={newBrand.en} onChange={(e) => setNewBrand({ ...newBrand, en: e.target.value })} placeholder="Brand (English)" data-testid="car-brand-name-en" className={`${inputCls} w-full`} dir="ltr" />
                            <div className="flex gap-2">
                                <button type="submit" data-testid="car-brand-submit" className={`${addBtnCls} flex-1 justify-center`}><Check className="w-3.5 h-3.5" /> إضافة الماركة</button>
                                <button type="button" onClick={() => setNewBrand(null)} className={`${iconBtnCls} hover:text-white`} aria-label="إلغاء"><X className="w-4 h-4" /></button>
                            </div>
                        </form>
                    ) : (
                        <button onClick={() => setNewBrand({ ar: "", en: "" })} data-testid="car-brand-add-button" className={`${addBtnCls} w-full justify-center`}>
                            <Plus className="w-3.5 h-3.5" /> إضافة ماركة سيارة
                        </button>
                    )}
                </div>

                <div className="border border-white/10 rounded-2xl p-4 space-y-2">
                    <p className="text-xs font-bold text-slate-300 mb-2">
                        الموديلات {selected ? `— ${selected.ar}` : ""}
                    </p>
                    {!draft ? (
                        <p className="text-[11px] text-slate-600">اختر ماركة سيارة لإدارة موديلاتها</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <input value={draft.ar} onChange={(e) => mutate((c) => { c.ar = e.target.value; })} data-testid="car-brand-edit-ar" className={inputCls} placeholder="الماركة (عربي)" />
                                <input value={draft.en} onChange={(e) => mutate((c) => { c.en = e.target.value; })} data-testid="car-brand-edit-en" className={inputCls} dir="ltr" placeholder="Brand (EN)" />
                            </div>
                            <div className="max-h-64 overflow-y-auto space-y-1.5 pl-1">
                                {draft.models.map((m, i) => (
                                    <div key={i} className={`rounded-xl px-3 py-2 border ${modelIdx === i ? "border-amber-400/60 bg-amber-400/10" : "border-white/5 bg-white/[0.02]"}`} data-testid="car-model-row">
                                        {editModel?.idx === i ? (
                                            <div className="flex gap-1.5">
                                                <input value={editModel.ar} onChange={(e) => setEditModel({ ...editModel, ar: e.target.value })} className={`${inputCls} flex-1 !py-1.5`} data-testid="car-model-edit-ar" />
                                                <input value={editModel.en} onChange={(e) => setEditModel({ ...editModel, en: e.target.value })} className={`${inputCls} flex-1 !py-1.5`} dir="ltr" data-testid="car-model-edit-en" />
                                                <button onClick={() => { mutate((c) => { c.models[i].ar = editModel.ar; c.models[i].en = editModel.en; }); setEditModel(null); }} data-testid="car-model-edit-save" className={`${iconBtnCls} hover:text-emerald-400`} aria-label="حفظ"><Check className="w-3.5 h-3.5" /></button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between gap-1">
                                                <button onClick={() => { setModelIdx(i); setEditTrim(null); }} data-testid="car-model-select" className="flex-1 text-right text-sm font-bold text-white truncate">
                                                    {m.ar} <span className="text-[10px] text-slate-500 font-normal" dir="ltr">{m.en}</span>
                                                </button>
                                                <button onClick={() => setEditModel({ idx: i, ar: m.ar, en: m.en })} data-testid="car-model-edit" className={`${iconBtnCls} hover:text-amber-400`} aria-label="تعديل"><Pencil className="w-3 h-3" /></button>
                                                <button onClick={() => { if (window.confirm(`حذف موديل «${m.ar}»؟`)) { mutate((c) => c.models.splice(i, 1)); setModelIdx(null); } }} data-testid="car-model-delete" className={`${iconBtnCls} hover:text-red-400`} aria-label="حذف"><Trash2 className="w-3 h-3" /></button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {draft.models.length === 0 && <p className="text-[11px] text-slate-600">لا توجد موديلات بعد</p>}
                            </div>
                            {newModel ? (
                                <form onSubmit={addModel} className="space-y-2 pt-2 border-t border-white/10" data-testid="car-model-form">
                                    <input required value={newModel.ar} onChange={(e) => setNewModel({ ...newModel, ar: e.target.value })} placeholder="الموديل (عربي)" data-testid="car-model-name-ar" className={`${inputCls} w-full`} />
                                    <input required value={newModel.en} onChange={(e) => setNewModel({ ...newModel, en: e.target.value })} placeholder="Model (English)" data-testid="car-model-name-en" className={`${inputCls} w-full`} dir="ltr" />
                                    <div className="flex gap-2">
                                        <button type="submit" data-testid="car-model-submit" className={`${addBtnCls} flex-1 justify-center`}><Plus className="w-3.5 h-3.5" /> إضافة الموديل</button>
                                        <button type="button" onClick={() => setNewModel(null)} className={`${iconBtnCls} hover:text-white`} aria-label="إلغاء"><X className="w-4 h-4" /></button>
                                    </div>
                                </form>
                            ) : (
                                <button onClick={() => setNewModel({ ar: "", en: "" })} data-testid="car-model-add-button" className={`${addBtnCls} w-full justify-center`}>
                                    <Plus className="w-3.5 h-3.5" /> إضافة موديل
                                </button>
                            )}
                        </>
                    )}
                </div>

                <div className="border border-white/10 rounded-2xl p-4 space-y-2">
                    <p className="text-xs font-bold text-slate-300 mb-2">
                        الفئات {draft && modelIdx !== null ? `— ${draft.models[modelIdx]?.ar || ""}` : ""}
                    </p>
                    {!draft || modelIdx === null || !draft.models[modelIdx] ? (
                        <p className="text-[11px] text-slate-600">اختر موديلاً لإدارة فئاته</p>
                    ) : (
                        <>
                            <div className="max-h-64 overflow-y-auto space-y-1.5 pl-1">
                                {(draft.models[modelIdx].trims || []).map((tr, i) => (
                                    <div key={i} className="rounded-xl px-3 py-2 border border-white/5 bg-white/[0.02]" data-testid="car-trim-row">
                                        {editTrim?.idx === i ? (
                                            <div className="flex gap-1.5">
                                                <input value={editTrim.ar} onChange={(e) => setEditTrim({ ...editTrim, ar: e.target.value })} className={`${inputCls} flex-1 !py-1.5`} data-testid="car-trim-edit-ar" />
                                                <input value={editTrim.en} onChange={(e) => setEditTrim({ ...editTrim, en: e.target.value })} className={`${inputCls} flex-1 !py-1.5`} dir="ltr" data-testid="car-trim-edit-en" />
                                                <button onClick={() => { mutate((c) => { c.models[modelIdx].trims[i] = { ar: editTrim.ar, en: editTrim.en }; }); setEditTrim(null); }} data-testid="car-trim-edit-save" className={`${iconBtnCls} hover:text-emerald-400`} aria-label="حفظ"><Check className="w-3.5 h-3.5" /></button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between gap-1">
                                                <p className="flex-1 text-sm font-bold text-white truncate">
                                                    {tr.ar} <span className="text-[10px] text-slate-500 font-normal" dir="ltr">{tr.en}</span>
                                                </p>
                                                <button onClick={() => setEditTrim({ idx: i, ar: tr.ar, en: tr.en })} data-testid="car-trim-edit" className={`${iconBtnCls} hover:text-amber-400`} aria-label="تعديل"><Pencil className="w-3 h-3" /></button>
                                                <button onClick={() => { if (window.confirm(`حذف فئة «${tr.ar}»؟`)) mutate((c) => c.models[modelIdx].trims.splice(i, 1)); }} data-testid="car-trim-delete" className={`${iconBtnCls} hover:text-red-400`} aria-label="حذف"><Trash2 className="w-3 h-3" /></button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {(draft.models[modelIdx].trims || []).length === 0 && <p className="text-[11px] text-slate-600">لا توجد فئات — أضف أول فئة</p>}
                            </div>
                            {newTrim ? (
                                <form onSubmit={addTrim} className="space-y-2 pt-2 border-t border-white/10" data-testid="car-trim-form">
                                    <input required value={newTrim.ar} onChange={(e) => setNewTrim({ ...newTrim, ar: e.target.value })} placeholder="الفئة (عربي)" data-testid="car-trim-name-ar" className={`${inputCls} w-full`} />
                                    <input required value={newTrim.en} onChange={(e) => setNewTrim({ ...newTrim, en: e.target.value })} placeholder="Trim (English)" data-testid="car-trim-name-en" className={`${inputCls} w-full`} dir="ltr" />
                                    <div className="flex gap-2">
                                        <button type="submit" data-testid="car-trim-submit" className={`${addBtnCls} flex-1 justify-center`}><Plus className="w-3.5 h-3.5" /> إضافة الفئة</button>
                                        <button type="button" onClick={() => setNewTrim(null)} className={`${iconBtnCls} hover:text-white`} aria-label="إلغاء"><X className="w-4 h-4" /></button>
                                    </div>
                                </form>
                            ) : (
                                <button onClick={() => setNewTrim({ ar: "", en: "" })} data-testid="car-trim-add-button" className={`${addBtnCls} w-full justify-center`}>
                                    <Plus className="w-3.5 h-3.5" /> إضافة فئة
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}
